import { rpcClient } from "@/api/WebsocketClient"
import SWR, { Call } from "@/api/SWR"
import { reactive } from "@vue/reactivity"
import SortAndFilterUtil from "@/util/SortAndFilterUtil"
import TaskBoard from '@/model/directory/TaskBoard';
import Page from '@/model/Page';
import { taskBoardStore } from '@/store/TaskBoardStore';

export default class GeneratedTaskBoardServiceApi {

    cache: Map<string, Call<any>> = new Map<string, Call<any>>()

    constructor() {
        this.init()
    }

    init() {
        window.setTimeout(() => {
            if (rpcClient) {
                rpcClient.apis.push(this)
            } else {
                this.init()
            }
        }, 1)
    }

    clearState() {
        this.cache = new Map<string, Call<any>>()
    }

    get connected(): boolean {
        return rpcClient.state.connected
    }

    _getTaskBoardsByProjectId(projectId: string): Promise<TaskBoard[]> {
        let rpcParams: any[] = Array.prototype.slice.call(arguments, 0, arguments.length).filter(arg => arg !== undefined)
        return rpcClient.call('getTaskBoardsByProjectId', rpcParams, false).then((data: any) => {
            if (data && Array.isArray(data)) {
                const taskBoards: TaskBoard[] = data.map(taskBoard => Object.assign(new TaskBoard(), taskBoard))
                taskBoardStore.replaceTaskBoards(taskBoards)
                return taskBoards
            } else return Promise.reject()
        })
    }


    _createTaskBoard(taskBoard: TaskBoard): Promise<TaskBoard[]> {
        let rpcParams: any[] = Array.prototype.slice.call(arguments, 0, arguments.length).filter(arg => arg !== undefined)
        return rpcClient.call('createTaskBoard', rpcParams, false).then((data: any) => {
            if (data && Array.isArray(data)) {
                const taskBoards: TaskBoard[] = data.map(taskBoard => Object.assign(new TaskBoard(), taskBoard))
                taskBoardStore.addOrReplaceTaskBoards(taskBoards)
                return taskBoards
            } else return Promise.reject()
        })
    }


    _updateTaskBoard(taskBoard: TaskBoard): Promise<string> {
        let rpcParams: any[] = Array.prototype.slice.call(arguments, 0, arguments.length).filter(arg => arg !== undefined)
        return rpcClient.call('updateTaskBoard', rpcParams, false).then((data: any) => {
            const model = Object.assign(new TaskBoard(), data)
            taskBoardStore.removeTaskBoard(taskBoard.originalId)
            taskBoardStore.addOrReplaceTaskBoard(model)
            return model.originalId
        })
    }

    _deleteTaskBoard(originalId: string): Promise<void> {
        let rpcParams: any[] = Array.prototype.slice.call(arguments, 0, arguments.length).filter(arg => arg !== undefined)
        return rpcClient.call('deleteTaskBoard', rpcParams, false).then(() => {
            taskBoardStore.removeTaskBoard(originalId)
        })
    }

    _setTaskBoardShares(originalId: string, arg1: any[] | null): Promise<void> {
        let rpcParams: any[] = Array.prototype.slice.call(arguments, 0, arguments.length).filter(arg => arg !== undefined)
        return rpcClient.call('setTaskBoardShares', rpcParams, false) as Promise<void>
    }

    _shareTaskBoard(originalId: string, arg1: any[] | null): Promise<void> {
        let rpcParams: any[] = Array.prototype.slice.call(arguments, 0, arguments.length).filter(arg => arg !== undefined)
        return rpcClient.call('shareTaskBoard', rpcParams, false) as Promise<void>
    }

    _getTaskBoards(): Promise<TaskBoard[]> {
        let rpcParams: any[] = Array.prototype.slice.call(arguments, 0, arguments.length).filter(arg => arg !== undefined)
        return rpcClient.call('getTaskBoards', rpcParams, true).then((data: any) => {
            if (data && Array.isArray(data)) {
                const taskBoards: TaskBoard[] = data.map(taskBoard => Object.assign(new TaskBoard(), taskBoard))
                taskBoardStore.replaceTaskBoards(taskBoards)
                return taskBoards
            } else return Promise.reject()
        })
    }


    getTaskBoards(refresh?: boolean | number): SWR<TaskBoard[], string[]> {
        //@ts-ignore
        const result: SWR<TaskBoard[], string[]> = reactive(new SWR<TaskBoard[], string[]>())
        const args: any[] = Array.prototype.slice.call(arguments, 0, 0).filter(arg => arg !== undefined)
        const callId: string = '_getTaskBoards' + JSON.stringify(args)
        const cached: Call<string[]> | undefined = this.cache.get(callId)
        if (refresh === undefined && cached && cached.ended) {
            for (const [ id, call ] of this.cache) {
                if (id.startsWith('_getTaskBoards[') && id !== callId && (!call.ended || call.ended > cached.ended)) {
                    refresh = 3000
                    break
                }
            }
        }
        if (cached && !cached.ended) {
            result.call = cached
            cached.promise?.then((data: string[]) => {
                const taskBoards: TaskBoard[] = []
                for (const id of data) {
                    const taskBoard: TaskBoard | undefined = taskBoardStore.state.taskBoards.get(id)
                    if (taskBoard) {
                        taskBoards.push(taskBoard)
                    }
                }
                result.data = taskBoards
            })
        } else if (refresh !== -1 && (!cached || refresh === true || (typeof refresh === 'number' && (cached.ended || 0) < (Date.now() - refresh)))) {
            const call = reactive(new Call<string[]>()) as Call<string[]>
            this.cache.set(callId, call)
            call.loading = !cached
            call.refreshing = !!cached
            //@ts-ignore since Array.filter does not provide nullsafe guard
            call.promise = this._getTaskBoards().then((data: TaskBoard[]) => {
                //@ts-ignore since Array.filter does not provide nullsafe guard
                result.data = data
                //@ts-ignore since Array.filter does not provide nullsafe guard
                call.data = data.map(taskBoard => taskBoard.originalId || '')
                return call.data
            }).catch(e => {
                setTimeout(() => {
                  this.cache.delete(callId)
                }, 1000)
                return Promise.reject(e)
            }).finally(() => {
                call.ended = Date.now()
                call.loading = false
                call.refreshing = false
            })
            result.call = call
        }
        let taskBoards: TaskBoard[] = [...taskBoardStore.state.taskBoards.values()]
        
        
        
        result.data = taskBoards
        return result
    }

    getTaskBoard(originalId: string): TaskBoard | undefined {
        return taskBoardStore.state.taskBoards.get(originalId)
    }
}
